(function () {
  'use strict';

  if (window.KpSidecartComboCro) return;
  window.KpSidecartComboCro = true;

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const maximumFractionDigits = Number.isInteger(value) ? 0 : 2;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits
    }).format(value).replace('₹', '₹\u00a0');
  };

  const comboSavingsForItem = (item) => (item.line_level_discount_allocations || [])
    .filter((allocation) => allocation.discount_application?.title === 'COMBO-OFFER')
    .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);

  class SidecartComboCro {
    constructor(drawer) {
      this.drawer = drawer;
      this.offer = drawer.querySelector('[data-kp-sidecart-offer]');
      this.recommendations = drawer.querySelector('[data-kp-sidecart-recommendations]');
      this.recommendationTrack = drawer.querySelector('[data-kp-sidecart-recommendations-track]');
      this.recommendationStatus = drawer.querySelector('[data-kp-sidecart-recommendations-status]');
      this.savings = drawer.querySelector('[data-kp-sidecart-savings]');
      this.hamperCard = drawer.querySelector('[data-kp-sidecart-hamper-card]');
      this.hamperAdd = drawer.querySelector('[data-kp-sidecart-hamper-add]');
      this.hamperQuantity = drawer.querySelector('[data-kp-sidecart-hamper-quantity]');
      this.hamperMinus = drawer.querySelector('[data-kp-sidecart-hamper-minus]');
      this.hamperPlus = drawer.querySelector('[data-kp-sidecart-hamper-plus]');
      this.cartContent = drawer.querySelector('.js-mini-cart-content');
      this.checkoutButton = drawer.querySelector('.js-cart-btn-checkout');
      this.minimumItems = Number(this.offer?.dataset.minimumItems || 3);
      this.discountPercentage = Number(this.offer?.dataset.discountPercentage || 20);
      this.offerEndDate = this.offer?.dataset.offerEndDate || '';
      this.offerExpiryCopy = this.offer?.querySelector('[data-kp-sidecart-offer-expiry]');
      this.productCache = new Map();
      this.recommendationProducts = null;
      this.syncTimer = null;
      this.isSyncing = false;
      this.hasPendingSync = false;
      this.isMutating = false;
      this.lastCart = null;
      this.wasOpen = this.isOpen();

      if (!this.offer || !this.recommendations || !this.cartContent) return;
      this.bind();
      if (this.isOpen()) this.scheduleSync(0);
    }

    isOpen() {
      return this.drawer.classList.contains('active') || this.drawer.classList.contains('open');
    }

    bind() {
      new MutationObserver(() => {
        const open = this.isOpen();
        if (open && !this.wasOpen) this.scheduleSync(40);
        this.wasOpen = open;
      }).observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

      new MutationObserver((mutations) => {
        const cartRowsWereRebuilt = mutations.some((mutation) => (
          Array.from(mutation.addedNodes || []).some((node) => (
            node.nodeType === 1
            && (node.matches?.('.mini-cart-item') || node.querySelector?.('.mini-cart-item'))
          ))
        ));
        if (cartRowsWereRebuilt && this.isOpen()) this.scheduleSync(60);
      }).observe(this.cartContent, { childList: true, subtree: true });

      this.cartContent.addEventListener('click', (event) => {
        if (!event.target.closest('.js-qty__adjust, .js-remove-mini-cart')) return;
        this.scheduleSync(700);
      });

      this.hamperAdd?.addEventListener('click', () => this.changeHamperQuantity(1));
      this.hamperMinus?.addEventListener('click', () => this.changeHamperQuantity(-1));
      this.hamperPlus?.addEventListener('click', () => this.changeHamperQuantity(1));

      document.addEventListener('kp:cart-updated', (event) => {
        const cart = event.detail?.cart || null;
        if (!this.isOpen()) {
          if (cart && Number(cart.item_count || 0) === 0) this.render(cart, []);
          return;
        }
        this.scheduleSync(40, cart);
      });

      this.recommendationTrack.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kp-sidecart-add]');
        if (!button) return;
        this.addRecommendation(button);
      });
    }

    scheduleSync(delay = 80, cart = null) {
      if (cart) this.lastCart = cart;
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => this.sync(), delay);
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Could not refresh the cart.');
      return response.json();
    }

    async fetchProduct(handle) {
      if (!handle) return null;
      if (this.productCache.has(handle)) return this.productCache.get(handle);

      const promise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null);

      this.productCache.set(handle, promise);
      return promise;
    }

    async isEligibleItem(item) {
      if (item.handle === 'rakhi-giftbox' || String(item.properties?._combo_excluded) === 'true') return false;
      const itemVendor = String(item.vendor || '').toLowerCase();
      if (itemVendor.includes('with gold')) return false;
      if (String(item.properties?._combo) === 'true') return true;

      const product = await this.fetchProduct(item.handle);
      const vendor = String(product?.vendor || item.vendor || '').toLowerCase();
      if (vendor.includes('with gold')) return false;

      const signal = [
        product?.title || item.product_title || item.title,
        product?.type || product?.product_type || item.product_type,
        ...(Array.isArray(product?.tags) ? product.tags : [])
      ].join(' ').toLowerCase();

      return signal.includes('pearl');
    }

    async getEligibleItems(cart) {
      const results = await Promise.all((cart.items || []).map(async (item) => ({
        item,
        eligible: await this.isEligibleItem(item)
      })));
      return results.filter((result) => result.eligible).map((result) => result.item);
    }

    async sync(cart = null) {
      if (!this.isOpen()) return;
      if (this.isSyncing) {
        if (cart) this.lastCart = cart;
        this.hasPendingSync = true;
        return;
      }
      this.isSyncing = true;

      try {
        let nextCart = cart || this.lastCart || await this.fetchCart();
        this.lastCart = null;
        let eligibleItems = await this.getEligibleItems(nextCart);
        const eligibleUnits = eligibleItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const needsPromotion = eligibleUnits >= this.minimumItems
          && eligibleItems.some((item) => String(item.properties?._combo) !== 'true');

        if (needsPromotion && !this.isMutating) {
          this.isMutating = true;
          nextCart = await this.promoteEligibleLines(nextCart, eligibleItems);
          eligibleItems = await this.getEligibleItems(nextCart);
          this.refreshThemeCart();
          this.isMutating = false;
        }

        this.render(nextCart, eligibleItems);
      } catch (error) {
        if (this.recommendationStatus) {
          this.recommendationStatus.textContent = error.message || 'Could not refresh combo savings.';
        }
      } finally {
        this.isMutating = false;
        this.isSyncing = false;
        if (this.hasPendingSync || this.lastCart) {
          this.hasPendingSync = false;
          this.scheduleSync(0);
        }
      }
    }

    async promoteEligibleLines(cart, eligibleItems) {
      let updatedCart = cart;

      for (const item of eligibleItems) {
        if (String(item.properties?._combo) === 'true') continue;
        const currentLineIndex = (updatedCart.items || []).findIndex((candidate) => (
          Number(candidate.variant_id || candidate.id) === Number(item.variant_id || item.id)
          && String(candidate.properties?._combo) !== 'true'
        ));
        if (currentLineIndex < 0) continue;

        const currentItem = updatedCart.items[currentLineIndex];
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: currentLineIndex + 1,
            quantity: currentItem.quantity,
            properties: {
              ...(currentItem.properties || {}),
              _combo: 'true',
              _combo_group: 'kp-primary-combo',
              _combo_source: 'sidecart'
            }
          })
        });
        if (!response.ok) throw new Error('Could not apply the combo offer.');
        updatedCart = await response.json();
      }

      return updatedCart;
    }

    render(cart, eligibleItems) {
      const eligibleUnits = eligibleItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const eligibleSubtotal = eligibleItems.reduce((sum, item) => (
        sum + Number(item.original_line_price || item.line_price || 0)
      ), 0);
      const comboSavings = (cart.items || []).reduce((sum, item) => sum + comboSavingsForItem(item), 0);
      const potentialSavings = Math.round((eligibleSubtotal * this.discountPercentage) / 100);

      if (!eligibleUnits) {
        this.drawer.classList.remove('has-kp-sidecart-offer');
        this.offer.hidden = true;
        this.recommendations.hidden = true;
        this.savings.hidden = true;
        this.restoreCheckoutLabel();
        this.decorateCartLines(cart, []);
        this.renderHamperControl(cart);
        return;
      }

      this.drawer.classList.add('has-kp-sidecart-offer');
      this.offer.hidden = false;
      const applied = comboSavings > 0;
      const ready = eligibleUnits >= this.minimumItems;
      const progress = Math.min(eligibleUnits / this.minimumItems, 1);
      const remaining = Math.max(this.minimumItems - eligibleUnits, 0);

      this.offer.classList.toggle('is-applied', applied);
      this.offer.classList.toggle('is-ready', ready && !applied);
      this.offer.querySelector('[data-kp-sidecart-offer-count]').textContent = applied
        ? '✓'
        : `${Math.min(eligibleUnits, this.minimumItems)}/${this.minimumItems}`;
      this.offer.querySelector('[data-kp-sidecart-offer-eyebrow]').textContent = applied
        ? 'COMBO-OFFER'
        : 'Combo savings';
      this.offer.querySelector('[data-kp-sidecart-offer-title]').textContent = applied
        ? `${this.discountPercentage}% combo offer applied`
        : ready
          ? `Your combo is ready for ${this.discountPercentage}% off`
        : remaining === 1
          ? `One more piece unlocks ${this.discountPercentage}% off`
          : `Add ${remaining} more pieces to unlock ${this.discountPercentage}% off`;
      this.offer.querySelector('[data-kp-sidecart-offer-detail]').textContent = applied
        ? `You saved ${money(comboSavings)} automatically. No code needed.`
        : ready
          ? 'Applying the offer automatically. No code needed.'
        : `${money(potentialSavings)} in savings is waiting. No code needed.`;
      this.offer.querySelector('[data-kp-sidecart-offer-progress-copy]').textContent = applied
        ? `${eligibleUnits} eligible pieces in your combo`
        : `${eligibleUnits} of ${this.minimumItems} eligible pieces`;
      this.renderOfferExpiry();

      const progressElement = this.offer.querySelector('[data-kp-sidecart-offer-progress]');
      progressElement.setAttribute('aria-valuenow', String(Math.min(eligibleUnits, this.minimumItems)));
      this.offer.querySelector('[data-kp-sidecart-offer-progress-fill]').style.width = `${progress * 100}%`;

      this.renderSavings(cart, comboSavings);
      this.decorateCartLines(cart, eligibleItems);
      this.renderHamperControl(cart);
      this.renderRecommendations(cart, applied);
    }

    renderSavings(cart, comboSavings) {
      if (!comboSavings) {
        this.savings.hidden = true;
        this.restoreCheckoutLabel();
        return;
      }

      this.savings.hidden = false;
      this.savings.querySelector('[data-kp-sidecart-original-total]').textContent = money(
        Number(cart.total_price || 0) + comboSavings
      );
      this.savings.querySelector('[data-kp-sidecart-savings-total]').textContent = `−${money(comboSavings)}`;
      if (this.checkoutButton) {
        this.checkoutButton.textContent = `CHECK OUT · SAVE ${money(comboSavings)}`;
      }
    }

    restoreCheckoutLabel() {
      if (this.checkoutButton) this.checkoutButton.textContent = 'CHECK OUT';
    }

    renderOfferExpiry() {
      if (!this.offerExpiryCopy) return;
      const expiresAt = this.offerEndDate
        ? new Date(`${this.offerEndDate}T23:59:59+05:30`)
        : null;
      const active = expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() > Date.now();
      this.offerExpiryCopy.textContent = active
        ? `Ends ${new Intl.DateTimeFormat('en-IN', {
            day: 'numeric',
            month: 'short',
            timeZone: 'Asia/Kolkata'
          }).format(expiresAt)}`
        : 'No code needed';
    }

    decorateCartLines(cart, eligibleItems) {
      const eligibleKeys = new Set(eligibleItems.map((item) => item.key));
      const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
      const itemNodes = this.cartContent.querySelectorAll('.mini-cart-item');

      itemNodes.forEach((node) => {
        const item = itemsByKey.get(node.dataset.cartKey);
        if (!item) return;
        node.querySelectorAll('.kp-sidecart-line-offer').forEach((element) => element.remove());

        const meta = node.querySelector('.mini-cart-meta');
        const price = meta?.querySelector(':scope > span');
        if (!meta || !price) return;

        const itemSaving = comboSavingsForItem(item);
        price.classList.toggle('kp-sidecart-line-price', itemSaving > 0);

        if (itemSaving > 0) {
          price.innerHTML = `<del>${money(item.original_price)}</del><strong>${money(item.final_price)}</strong>`;
        } else {
          price.textContent = money(item.price);
        }

        if (itemSaving > 0 || eligibleKeys.has(item.key)) {
          const badge = document.createElement('small');
          badge.className = 'kp-sidecart-line-offer';
          badge.textContent = itemSaving > 0
            ? `Combo saved ${money(itemSaving)}`
            : `Eligible for ${this.discountPercentage}% combo`;
          meta.appendChild(badge);
        }
      });
    }

    isRakhiAddon(item) {
      return item?.handle === 'rakhi-giftbox'
        || String(item?.properties?._rakhi_addon) === 'true';
    }

    renderHamperControl(cart) {
      const items = cart.items || [];
      const addonItems = items.filter((item) => this.isRakhiAddon(item));
      const regularItems = items.filter((item) => !this.isRakhiAddon(item));
      const quantity = addonItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      if (this.hamperCard) this.hamperCard.hidden = regularItems.length === 0 && quantity === 0;
      if (this.hamperQuantity) this.hamperQuantity.textContent = String(quantity);
      if (this.hamperAdd) {
        this.hamperAdd.hidden = quantity > 0;
        this.hamperAdd.disabled = this.isMutating;
      }
      const quantityControl = this.hamperQuantity?.closest('.kp-sidecart-hamper-card__quantity');
      if (quantityControl) quantityControl.hidden = quantity === 0;
      if (this.hamperMinus) this.hamperMinus.disabled = this.isMutating || quantity === 0;
      if (this.hamperPlus) this.hamperPlus.disabled = this.isMutating;
    }

    async changeHamperQuantity(change) {
      if (this.isMutating || !change) return;
      this.isMutating = true;
      if (this.hamperAdd) this.hamperAdd.disabled = true;
      if (this.hamperMinus) this.hamperMinus.disabled = true;
      if (this.hamperPlus) this.hamperPlus.disabled = true;

      try {
        let cart = await this.fetchCart();
        const addonItems = (cart.items || []).filter((item) => this.isRakhiAddon(item));

        if (change > 0) {
          const hamper = await this.fetchProduct('rakhi-giftbox');
          const variant = hamper?.variants?.find((candidate) => candidate.available);
          if (!variant) throw new Error('The Rakhi Giftbox is currently unavailable.');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{
                id: Number(variant.id),
                quantity: 1,
                properties: {
                  _rakhi_addon: 'true',
                  _rakhi_source: 'sidecart',
                  _combo_excluded: 'true'
                }
              }]
            })
          });
          if (!response.ok) throw new Error('Could not add the Rakhi Giftbox.');
        } else {
          const target = addonItems.find((item) => (
            item.properties?._rakhi_source === 'sidecart'
            && !item.properties?._rakhi_parent_group
            && !item.properties?._rakhi_parent_key
            && !item.properties?._rakhi_parent_variant
          )) || addonItems[addonItems.length - 1];
          if (!target) return;

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: target.key,
              quantity: Math.max(0, Number(target.quantity || 0) - 1)
            })
          });
          if (!response.ok) throw new Error('Could not remove the Rakhi Giftbox.');
        }

        cart = await this.fetchCart();
        window.theme?.miniCart?.refreshCart?.(cart, false);
        document.dispatchEvent(new CustomEvent('kp:cart-updated', { detail: { cart } }));
        this.drawer.classList.add('active');
        this.renderHamperControl(cart);
      } catch (error) {
        window.alert(error.message || 'Could not update the Rakhi Giftbox.');
      } finally {
        this.isMutating = false;
        this.scheduleSync(0);
      }
    }

    async loadRecommendations() {
      if (this.recommendationProducts) return this.recommendationProducts;

      this.recommendationProducts = fetch('/collections/best-sellers/products.json?limit=40', {
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : { products: [] })
        .then((payload) => (payload.products || []).filter((product) => {
          const vendor = String(product.vendor || '').toLowerCase();
          const signal = [product.title, product.product_type, ...(product.tags || [])].join(' ').toLowerCase();
          const availableVariants = (product.variants || []).filter((variant) => variant.available);
          return !vendor.includes('with gold') && signal.includes('pearl') && availableVariants.length === 1;
        }))
        .catch(() => []);

      return this.recommendationProducts;
    }

    async renderRecommendations(cart, applied) {
      if (applied) {
        this.recommendations.hidden = true;
        this.recommendationTrack.innerHTML = '';
        return;
      }

      const products = await this.loadRecommendations();
      if (!this.isOpen()) return;

      const cartHandles = new Set((cart.items || []).map((item) => item.handle));
      const recommendations = products.filter((product) => !cartHandles.has(product.handle)).slice(0, 4);

      if (!recommendations.length) {
        this.recommendations.hidden = true;
        return;
      }

      this.recommendations.hidden = false;
      this.recommendations.querySelector('[data-kp-sidecart-recommendations-title]').textContent = 'Complete your combo';
      this.recommendations.querySelector('[data-kp-sidecart-recommendations-copy]').textContent = 'Popular pairings';

      this.recommendationTrack.innerHTML = recommendations.map((product) => {
        const variant = product.variants.find((candidate) => candidate.available);
        const price = Math.round(Number.parseFloat(variant.price || 0) * 100);
        const comboPrice = Math.round(price * (100 - this.discountPercentage) / 100);
        const image = product.images?.[0]?.src || '';
        const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=180` : '';
        const productUrl = `/products/${encodeURIComponent(product.handle)}`;
        const safeTitle = this.escapeHtml(product.title);

        return `
          <article class="kp-sidecart-rec-card">
            <a class="kp-sidecart-rec-card__image" href="${productUrl}" aria-label="View ${safeTitle}">
              <img src="${this.escapeAttribute(imageUrl)}" alt="" loading="lazy" width="74" height="74">
            </a>
            <div class="kp-sidecart-rec-card__body">
              <a class="kp-sidecart-rec-card__title" href="${productUrl}">${safeTitle}</a>
              <div class="kp-sidecart-rec-card__prices">
                <span>${money(price)}</span>
                <strong>${money(comboPrice)} with combo</strong>
              </div>
              <button
                class="kp-sidecart-rec-card__add"
                type="button"
                data-kp-sidecart-add
                data-variant-id="${variant.id}"
                data-product-title="${this.escapeAttribute(product.title)}"
                aria-label="Add ${this.escapeAttribute(product.title)} to combo"
              >Add</button>
            </div>
          </article>
        `;
      }).join('');
    }

    async addRecommendation(button) {
      if (this.isMutating || button.disabled) return;
      this.isMutating = true;
      button.disabled = true;
      const originalLabel = button.textContent;
      button.textContent = 'Adding…';
      if (this.recommendationStatus) this.recommendationStatus.textContent = '';

      try {
        let cart = await this.fetchCart();
        const eligibleItems = await this.getEligibleItems(cart);
        cart = await this.promoteEligibleLines(cart, eligibleItems);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(button.dataset.variantId),
              quantity: 1,
              properties: {
                _combo: 'true',
                _combo_group: 'kp-primary-combo',
                _combo_source: 'sidecart'
              }
            }]
          })
        });
        if (!response.ok) throw new Error('Could not add this product.');

        cart = await this.fetchCart();
        this.refreshThemeCart();
        document.dispatchEvent(new CustomEvent('kp:cart-updated', { detail: { cart } }));
        const eligibleAfterAdd = await this.getEligibleItems(cart);
        this.render(cart, eligibleAfterAdd);
        if (this.recommendationStatus) {
          this.recommendationStatus.textContent = `${button.dataset.productTitle} added to your combo.`;
        }
      } catch (error) {
        button.disabled = false;
        button.textContent = originalLabel;
        if (this.recommendationStatus) {
          this.recommendationStatus.textContent = error.message || 'Could not update your combo.';
        }
      } finally {
        this.isMutating = false;
      }
    }

    refreshThemeCart() {
      window.theme?.miniCart?.updateElements?.();
      window.theme?.miniCart?.generateCart?.();
      this.drawer.classList.add('active');
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = String(value || '');
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  }

  const init = () => {
    document.querySelectorAll('.js-mini-cart.cardraw').forEach((drawer) => {
      if (drawer.dataset.kpSidecartComboInitialized === 'true') return;
      drawer.dataset.kpSidecartComboInitialized = 'true';
      new SidecartComboCro(drawer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
